"use client";

import { Database, CheckCircle2, Clock, AlertCircle, RefreshCw } from "lucide-react";
import type { CampaignFormData, ClueFormData, AtomicTemplateOption, AtomicSchemaInfo } from "./types";

interface ReviewStepProps {
  campaignData: CampaignFormData;
  clues: ClueFormData[];
  originalStatus?: string;
  atomicTemplates?: AtomicTemplateOption[];
  atomicSchemas?: AtomicSchemaInfo[];
}

export function ReviewStep({
  campaignData,
  clues,
  originalStatus,
  atomicTemplates = [],
  atomicSchemas = [],
}: ReviewStepProps) {
  const getSelectedTemplate = (atomicTemplateId?: string) => {
    if (!atomicTemplateId) return undefined;
    return atomicTemplates.find(t => t.id === atomicTemplateId);
  };

  const getSchemaStatusIcon = (status: string) => {
    switch (status) {
      case "CREATED":
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case "CREATING":
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case "FAILED":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getSchemaStatusLabel = (status: string) => {
    switch (status) {
      case "CREATED":
        return "On-chain";
      case "CREATING":
        return "Syncing...";
      case "FAILED":
        return "Failed";
      default:
        return "Draft";
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Review Campaign</h2>

      <div className="bg-gray-50 p-4 rounded-lg border border-gray-300">
        <h3 className="font-medium text-gray-900 mb-2">{campaignData.title}</h3>
        <p className="text-gray-700 mb-4">{campaignData.description}</p>

        <div className="grid grid-cols-2 gap-4 text-sm text-gray-900">
          <div>
            <span className="font-medium">Type:</span>{" "}
            {campaignData.campaignType === "SINGLE_LOCATION"
              ? "Single Location"
              : "Multi-Location"}
          </div>
          <div>
            <span className="font-medium">Location:</span>{" "}
            {(() => {
              const parts: string[] = [];
              if (campaignData.streetAddress) parts.push(campaignData.streetAddress);
              if (campaignData.city && campaignData.state) {
                parts.push(`${campaignData.city}, ${campaignData.state}`);
              } else if (campaignData.city || campaignData.state) {
                parts.push(campaignData.city || campaignData.state);
              }
              if (campaignData.zipCode) parts.push(campaignData.zipCode);
              return parts.join(', ') || "Not specified";
            })()}
          </div>
          <div>
            <span className="font-medium">
              {campaignData.campaignType === "SINGLE_LOCATION" ? "Checkpoints" : "Locations"}:
            </span>{" "}
            {clues.length}
          </div>
          <div>
            <span className="font-medium">Status:</span>{" "}
            {originalStatus || "Draft"}
          </div>
        </div>
      </div>

      {atomicSchemas.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Database className="w-5 h-5 text-blue-600" />
            <h4 className="font-medium text-gray-900">Assigned Blockchain Schema</h4>
          </div>
          <div className="space-y-2">
            {atomicSchemas.map((schema) => (
              <div
                key={schema.id}
                className="flex items-center justify-between bg-white p-3 rounded border border-blue-100"
              >
                <div className="flex items-center gap-3">
                  {getSchemaStatusIcon(schema.status)}
                  <div>
                    <span className="font-mono text-sm font-medium text-gray-900">
                      {schema.schemaName}
                    </span>
                    <span className="ml-2 text-xs text-gray-500">
                      ({schema.templatesCount} template{schema.templatesCount !== 1 ? "s" : ""})
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    schema.status === "CREATED"
                      ? "bg-green-100 text-green-700"
                      : schema.status === "CREATING"
                      ? "bg-blue-100 text-blue-700"
                      : schema.status === "FAILED"
                      ? "bg-red-100 text-red-700"
                      : "bg-gray-100 text-gray-600"
                  }`}>
                    {getSchemaStatusLabel(schema.status)}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-blue-600 mt-2">
            Schemas define the NFT structure for this campaign on the WAX blockchain.
          </p>
        </div>
      )}

      <div>
        <h4 className="font-medium text-gray-900 mb-2">
          {campaignData.campaignType === "SINGLE_LOCATION" ? "Checkpoints:" : "Locations:"}
        </h4>
        <div className="space-y-2">
          {clues.map((clue, index) => {
            const template = clue.atomicTemplateId ? getSelectedTemplate(clue.atomicTemplateId) : null;
            return (
              <div key={clue.id || index} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-300">
                <span className="text-gray-900">{index + 1}. {clue.title}</span>
                <div className="flex items-center gap-2">
                  {template && (
                    <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                      NFT: {template.name}
                    </span>
                  )}
                  {campaignData.campaignType !== "SINGLE_LOCATION" && (
                    <span className="text-sm text-gray-600">{clue.address}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
